import { db } from "../firebaseClient"
import { doc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { logPlatformEvent } from "./eventHelpers"

// Minimal Programs schema — enough to drive the "Programs with upcoming deadlines"
// signal on the dashboard and the future Programs list. Deliberately thin: just
// enough fields to be real, expand later (modules, enrollment linkage, etc.)
export const createProgram = async ({ orgId, title, description, deadlineAt, status, programType, courseIds }) => {
  if (!orgId || !title?.trim()) {
    return { programId: null, error: { message: "Org ID and title are required" } }
  }
  try {
    const programRef = doc(collection(db, "organizations", orgId, "programs"))
    await setDoc(programRef, {
      title: title.trim(),
      description: description || "",
      status: status || "active", // "active" | "archived"
      // Free-text classification so a "program" can represent a Bootcamp, Academy,
      // Certification, Workshop, Membership, etc. without a new collection per type.
      programType: programType || "Program",
      // Bundles existing courseIds — a program's Learners/Completion/Revenue are
      // aggregated across whichever courses are listed here, rather than requiring
      // a new link on every enrollment going forward.
      courseIds: courseIds || [],
            deadlineAt: deadlineAt || null, // ISO string or Firestore Timestamp
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    logPlatformEvent({
      type: "PROGRAM_CREATED",
      orgId,
      targetPath: `organizations/${orgId}/programs/${programRef.id}`,
      metadata: { title: title.trim(), programType: programType || "Program" },
    })
    return { programId: programRef.id, error: null }
  } catch (error) {
    return { programId: null, error }
  }
}

export const updateProgram = async (orgId, programId, updates) => {
  try {
    const programRef = doc(db, "organizations", orgId, "programs", programId)
    await updateDoc(programRef, { ...updates, updatedAt: serverTimestamp() })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const deleteProgram = async (orgId, programId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "programs", programId))
    logPlatformEvent({
      type: "PROGRAM_DELETED",
      orgId,
      targetPath: `organizations/${orgId}/programs/${programId}`,
      severity: "warning",
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
} 

export const listPrograms = async (orgId) => {
  try {
    const programsRef = collection(db, "organizations", orgId, "programs")
    const snap = await getDocs(programsRef)
    const programs = snap.docs?.map((d) => ({ programId: d.id, ...d.data() }))
    return { programs, error: null }
  } catch (error) {
    return { programs: [], error }
  }
}